import React, { useState } from 'react';
import { Bed, Bath, Sofa, TreeDeciduous, Accessibility, Scissors, Shirt, Wifi, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

const facilities = [
  { icon: Bed, label: 'Comfortable single bedrooms' },
  { icon: Bath, label: 'En-suite facilities available' },
  { icon: Sofa, label: 'Spacious lounges and dining areas' },
  { icon: TreeDeciduous, label: 'Conservatory and garden spaces' },
  { icon: Accessibility, label: 'Wheelchair accessibility throughout' },
  { icon: Bath, label: 'Assisted bathing facilities' },
  { icon: Scissors, label: 'Hairdressing services' },
  { icon: Shirt, label: 'Laundry and housekeeping' },
  { icon: Wifi, label: 'Wi-Fi and entertainment options' },
];

const galleryImages = [
  {
    url: 'https://customer-assets.emergentagent.com/job_github-sync-86/artifacts/szjndv8h_2.jpg',
    caption: 'Comfortable Bedrooms',
  },
  {
    url: 'https://customer-assets.emergentagent.com/job_github-sync-86/artifacts/hvralmol_15188_2204240158018.webp',
    caption: 'Beautiful Garden Spaces',
  },
  {
    url: 'https://customer-assets.emergentagent.com/job_github-sync-86/artifacts/8y44buel_IMG_1319.jpeg',
    caption: 'Homely Environment',
  },
];

const FacilitiesSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % galleryImages.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  return (
    <section
      id="facilities"
      className="py-20 md:py-32 bg-slate-50"
      data-testid="facilities-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#c9a227]/10 rounded-full text-[#c9a227] text-sm font-medium mb-6">
            Facilities & Environment
          </div>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1e3a5f] mb-6"
           
          >
            Designed for Comfort and Wellbeing
          </h2>
          <p className="text-slate-600 text-lg">
            Our home offers a range of facilities to ensure comfort, safety and enjoyment for all our residents.
          </p>
        </div>

        {/* Image Carousel + Facilities Grid */}
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Image Carousel */}
          <div className="relative" data-testid="facilities-carousel">
            {/* Main Image */}
            <div className="relative rounded-2xl overflow-hidden shadow-xl">
              <img
                src={galleryImages[currentSlide].url}
                alt={galleryImages[currentSlide].caption}
                className="w-full h-80 lg:h-96 object-cover transition-all duration-500"
              />
              {/* Caption Overlay */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-6">
                <p className="text-white font-medium text-lg">{galleryImages[currentSlide].caption}</p>
              </div>
              
              {/* Navigation Arrows */}
              <Button
                variant="ghost"
                size="icon"
                onClick={prevSlide}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-[#1e3a5f] rounded-full shadow-lg"
                data-testid="carousel-prev-btn"
              >
                <ChevronLeft className="w-6 h-6" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={nextSlide}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-[#1e3a5f] rounded-full shadow-lg"
                data-testid="carousel-next-btn"
              >
                <ChevronRight className="w-6 h-6" />
              </Button>
            </div>

            {/* Thumbnail Dots */}
            <div className="flex justify-center gap-2 mt-4">
              {galleryImages.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index === currentSlide
                      ? 'bg-[#c9a227] w-8'
                      : 'bg-slate-300 hover:bg-slate-400'
                  }`}
                  data-testid={`carousel-dot-${index}`}
                />
              ))}
            </div>

            {/* Thumbnail Strip */}
            <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
              {galleryImages.map((image, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`flex-shrink-0 rounded-lg overflow-hidden border-2 transition-all duration-300 ${
                    index === currentSlide
                      ? 'border-[#c9a227]'
                      : 'border-transparent opacity-60 hover:opacity-100'
                  }`}
                >
                  <img
                    src={image.url}
                    alt={image.caption}
                    className="w-20 h-14 object-cover"
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Facilities List */}
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-100">
            <h3
              className="text-2xl font-bold text-[#1e3a5f] mb-6"
             
            >
              Our Facilities
            </h3>
            <div className="space-y-4">
              {facilities.map((facility, index) => {
                const IconComponent = facility.icon;
                return (
                  <div
                    key={facility.label}
                    className="flex items-center gap-4 p-3 rounded-xl hover:bg-slate-50 transition-colors"
                    data-testid={`facility-item-${index}`}
                  >
                    <div className="w-10 h-10 rounded-lg bg-[#1e3a5f]/10 flex items-center justify-center flex-shrink-0">
                      <IconComponent className="w-5 h-5 text-[#1e3a5f]" />
                    </div>
                    <span className="text-slate-700">{facility.label}</span>
                  </div>
                );
              })}
            </div>
            <p className="mt-6 text-sm text-slate-500 italic">
              Every space is designed to feel welcoming, safe and homely.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FacilitiesSection;
